import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FlightBooKaro',
  url: 'https://flightbookkaro.com',
  foundingDate: '2019',
  description: 'Flight booking platform trusted by 2M+ travelers. Compare all major US airlines and book in one click.',
  contactPoint: { '@type': 'ContactPoint', email: 'support@flightbookkaro.com', contactType: 'customer support', availableLanguage: 'English' },
};

const TEAM = [
  { name: 'Alex Carter', role: 'CEO & Co-Founder', avatar: 'AC', color: 'bg-violet-600' },
  { name: 'Maya Patel', role: 'CTO & Co-Founder', avatar: 'MP', color: 'bg-indigo-600' },
  { name: 'Jordan Lee', role: 'Head of Product', avatar: 'JL', color: 'bg-purple-600' },
  { name: 'Sam Rivera', role: 'Head of Customer Success', avatar: 'SR', color: 'bg-pink-600' },
];

const STATS = [
  { value: '2M+', label: 'Happy Travelers' },
  { value: '500+', label: 'Routes Covered' },
  { value: '8', label: 'Airline Partners' },
  { value: '2019', label: 'Founded' },
];

export default function AboutPage() {
  return (
    <div>
      <SEO
        title="About Us"
        canonical="/about"
        description="Learn about FlightBooKaro — founded in 2019 by frequent flyers to make air travel transparent and affordable. Trusted by 2M+ US travelers."
        keywords="about flightbookkaro, flight booking company, who we are, our story, travel platform"
        jsonLd={aboutJsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-900 to-purple-800 text-white py-14 sm:py-20 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">About FlightBooKaro</h1>
        <p className="text-violet-100 text-base sm:text-lg max-w-2xl mx-auto">
          We're on a mission to make air travel accessible, transparent, and affordable for every American traveler.
        </p>
      </div>

      {/* Stats */}
      <div className="bg-white py-10 sm:py-12 px-4 border-b border-gray-100">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8 text-center">
          {STATS.map(s => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold text-violet-600">{s.value}</p>
              <p className="text-gray-500 text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Story */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div>
            <p className="text-violet-600 font-semibold text-sm uppercase tracking-wide mb-2">Our Story</p>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Built by Travelers, for Travelers</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              FlightBooKaro was founded in 2019 by a team of frequent flyers frustrated by hidden fees, confusing booking flows, and inconsistent pricing across platforms.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              We built the platform we always wished existed — one that shows you the true cost upfront, lets you compare all major US airlines in one place, and confirms your booking in under 3 minutes.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Today, over 2 million travelers trust FlightBooKaro to get the best fares on domestic and international routes from the US.
            </p>
          </div>
          <div className="bg-violet-50 rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">✈️</div>
            <p className="text-violet-700 font-bold text-xl">"Fly Smarter. Pay Less."</p>
            <p className="text-gray-500 text-sm mt-2">Our founding principle since day one.</p>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-violet-600 font-semibold text-sm uppercase tracking-wide mb-2">The People</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Meet Our Team</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {TEAM.map(m => (
              <div key={m.name} className="bg-white rounded-2xl p-4 sm:p-6 text-center shadow-sm border border-gray-100">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full ${m.color} text-white flex items-center justify-center font-bold text-base sm:text-xl mx-auto mb-3`}>
                  {m.avatar}
                </div>
                <p className="font-bold text-gray-800">{m.name}</p>
                <p className="text-gray-500 text-xs mt-1">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-14 px-4 text-center">
        <h3 className="text-2xl font-bold text-gray-800 mb-3">Ready to fly with us?</h3>
        <Link to="/" className="inline-block bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
          Search Flights →
        </Link>
      </div>
    </div>
  );
}
